// Detect when Globo Smart Filter & Search fails to render on a collection page.
//
// Globo is an app embed with `productGridSelector: ""` (settings_data.json), so it
// auto-detects the theme's grid at runtime rather than using a pinned selector — the most
// fragile configuration it offers. When it works it injects #gf-tree (filter sidebar) and
// #gf-grid (results wrapper), and ADOPTS Dawn's own <ul id="product-grid">, re-stamping it as
// #gf-products. When it doesn't, nothing in the theme says so: main-collection-product-grid.liquid
// emits #ProductGridContainer and #product-grid either way, so the theme's own elements are
// worthless as a health signal. Only the #gf-* ids tell you anything.
//
// On failure this warns, records the state on <html>/window/a CustomEvent, and fires a
// throttled fire-and-forget ntfy notification. It never blocks, never renders anything, and
// never surfaces an error of its own — a broken health check must not break the page.

(function () {
  'use strict';

  const NTFY_ENDPOINT = 'https://notify-app.maxilitecloudsolutions.store/globo-alert';
  const MOUNT_TIMEOUT_MS = 10000; // how long Globo gets to mount before we call it dead
  const ALERT_COOLDOWN_MS = 30 * 60 * 1000;
  const ALERT_COOLDOWN_KEY = 'maxilite_globo_alert_at';
  const LOG = '[maxilite-globo-health]';

  // Same three-way gate as consent-logger.js / cookie-consent.js: query param, localStorage,
  // or a window flag. Verbose logging only; failures always warn regardless (see report()).
  const DEBUG = (function () {
    try {
      if (/[?&]globo_health_debug=1/.test(window.location.search)) return true;
      if (window.localStorage && localStorage.getItem('globoHealthDebug') === '1') return true;
    } catch (e) {}
    return window.GLOBO_HEALTH_DEBUG === true;
  })();

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, [LOG].concat(Array.prototype.slice.call(arguments)));
    }
  }

  // Resolves with the element, rejects on timeout. Three differences from the obvious version:
  // MutationObserver is feature-detected, the timer is cleared on resolve (otherwise it fires
  // and disconnects an already-disconnected observer long after we're done), and there is
  // exactly ONE observer for the whole check — Globo mounts node by node, so a subtree observer
  // on document.body per selector would triple the callback churn at the busiest moment of
  // page load.
  function waitFor(selector, timeout) {
    return new Promise(function (resolve, reject) {
      const found = document.querySelector(selector);
      if (found) return resolve(found);
      if (typeof window.MutationObserver !== 'function') {
        return reject(new Error('MutationObserver unavailable'));
      }

      let timer = 0;
      const obs = new window.MutationObserver(function () {
        const el = document.querySelector(selector);
        if (!el) return;
        clearTimeout(timer);
        obs.disconnect();
        resolve(el);
      });
      obs.observe(document.body, { childList: true, subtree: true });

      timer = setTimeout(function () {
        obs.disconnect();
        reject(new Error(selector + ' not found'));
      }, timeout);
    });
  }

  // Globo's adopted <ul> holds product <li>s, but an empty result also carries a plain <div>
  // ("no_results" app snippet), so count elements by tag rather than trusting children.length.
  function countProductItems(el) {
    if (!el || !el.children) return 0;
    let n = 0;
    for (let i = 0; i < el.children.length; i++) {
      if (el.children[i].tagName === 'LI') n++;
    }
    return n;
  }

  function report(status, extra) {
    const detail = Object.assign(
      {
        status: status,
        url: window.location.pathname + window.location.search,
        shop: (window.Shopify && window.Shopify.shop) || null,
        ts: new Date().toISOString(),
      },
      extra || {}
    );

    try {
      document.documentElement.dataset.globoHealth = status;
    } catch (e) {}
    window.MaxiliteGloboHealth = detail;
    try {
      document.dispatchEvent(new CustomEvent('maxiliteGloboHealth', { detail: detail }));
    } catch (e) {}

    if (status === 'failed' || status === 'partial') {
      // Never gated behind DEBUG — a health check that stays quiet about failures by default
      // defeats its own purpose.
      console.warn(LOG, status.toUpperCase(), detail);
      notify(detail);
    } else {
      log(status, detail);
    }
    return detail;
  }

  // If Globo is down site-wide, every shopper on every collection page would fire an alert.
  // One per browser per cooldown window is enough to know. DEBUG bypasses it so the alert can
  // actually be tested.
  function alertAllowed() {
    if (DEBUG) return true;
    try {
      const last = parseInt(localStorage.getItem(ALERT_COOLDOWN_KEY) || '0', 10);
      if (last && Date.now() - last < ALERT_COOLDOWN_MS) return false;
      localStorage.setItem(ALERT_COOLDOWN_KEY, String(Date.now()));
    } catch (e) {
      // Private mode or blocked storage: no throttle available. Alerting without one beats
      // going silent, and these sessions are a small minority.
    }
    return true;
  }

  function buildMessage(d) {
    const present =
      [d.tree && '#gf-tree', d.grid && '#gf-grid', d.products && '#gf-products']
        .filter(Boolean)
        .join(', ') || '(none)';

    return [
      'Globo filter ' + (d.status === 'partial' ? 'PARTIALLY rendered' : 'FAILED to render'),
      '',
      'Page:    ' + d.url,
      'Present: ' + present,
      'Waited:  ' + d.ms + 'ms',
      'Products in page source: ' + d.nativeCount,
      'Shop:    ' + (d.shop || 'n/a'),
      'Time:    ' + d.ts,
      'UA:      ' + String(navigator.userAgent || '').slice(0, 160),
    ].join('\n');
  }

  function notify(detail) {
    if (!alertAllowed()) {
      log('alert suppressed by cooldown');
      return;
    }
    const body = buildMessage(detail);

    // text/plain is load-bearing: it keeps this a CORS "simple request", so the browser never
    // sends a preflight OPTIONS that the notify host would have to answer. keepalive lets the
    // POST survive the shopper navigating away mid-flight. The two-argument .then() form (not
    // .catch) means a rejection is always handled — this must never produce an unhandled
    // rejection in a shopper's console.
    try {
      if (window.fetch) {
        window
          .fetch(NTFY_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            body: body,
            keepalive: true,
          })
          .then(
            function (res) {
              log('alert sent, status', res && res.status);
            },
            function (err) {
              log('alert POST failed, falling back to sendBeacon', err);
              beacon(body);
            }
          );
        return;
      }
    } catch (e) {
      log('alert threw', e);
    }
    beacon(body);
  }

  function beacon(body) {
    try {
      if (navigator.sendBeacon) {
        const ok = navigator.sendBeacon(
          NTFY_ENDPOINT,
          new Blob([body], { type: 'text/plain;charset=UTF-8' })
        );
        log('sendBeacon queued:', ok);
      }
    } catch (e) {
      log('sendBeacon error', e);
    }
  }

  function run() {
    // Snapshot the native grid BEFORE Globo touches it. Safe because this script is deferred:
    // it runs at DOMContentLoaded, while <ul id="product-grid"> still has its original id.
    // Once Globo adopts it the id becomes gf-products and this reading is impossible.
    const nativeGrid = document.getElementById('product-grid');
    // main-collection-product-grid.liquid renders the empty-collection branch with
    // id="product-grid" on a DIV, not a UL — hence the tagName check, not just a null check.
    const nativeIsGrid = !!nativeGrid && nativeGrid.tagName === 'UL';
    const nativeCount = nativeIsGrid ? nativeGrid.children.length : 0;

    if (window.Shopify && window.Shopify.designMode) {
      report('skipped', { reason: 'design-mode', nativeCount: nativeCount });
      return;
    }

    // Nothing for Globo to adopt, so its absence is not a fault. Note this stays true with
    // hide-content enabled: that setting blanks the card bodies but still emits the <li>s.
    if (!nativeIsGrid || nativeCount === 0) {
      report('skipped', { reason: 'empty-collection', nativeCount: nativeCount });
      return;
    }

    const startedAt = (window.performance && performance.now()) || 0;
    const elapsed = function () {
      return Math.round(((window.performance && performance.now()) || 0) - startedAt);
    };

    // #gf-products is the primary signal because it is the LAST thing Globo produces — the
    // grid adopt. If it appears, Globo finished. If it doesn't, #gf-tree/#gf-grid tell us
    // whether nothing mounted at all or only part of it did.
    waitFor('#gf-products', MOUNT_TIMEOUT_MS).then(
      function (el) {
        report('ok', {
          ms: elapsed(),
          tree: !!document.getElementById('gf-tree'),
          grid: !!document.getElementById('gf-grid'),
          products: true,
          productCount: countProductItems(el),
          nativeCount: nativeCount,
        });
      },
      function () {
        const tree = !!document.getElementById('gf-tree');
        const grid = !!document.getElementById('gf-grid');
        report(tree || grid ? 'partial' : 'failed', {
          ms: elapsed(),
          tree: tree,
          grid: grid,
          products: false,
          productCount: 0,
          nativeCount: nativeCount,
        });
      }
    );

    log('watching for Globo — inspect window.MaxiliteGloboHealth once it settles');
  }

  run();
})();
